// lib
import React from 'react';
import styled from 'styled-components';

// twicapp-ui
import Headline2 from '@joinforma/ui/components/Headline2';
import Title1 from '@joinforma/ui/components/Title1';
import Body2 from '@joinforma/ui/components/Body2';
import Button from '@joinforma/ui/components/Button';
import Modal from '@joinforma/ui/components/Modal';
import SvgIcon from '@joinforma/ui/components/SvgIcon';

import {
  ButtonVariant,
  SvgIconSize,
  SvgIconColor,
} from '@joinforma/ui/constants/properties';

import { ReactComponent as TopRightSvg } from '@joinforma/ui/assets/icons/arrow-top-right.svg';
import { ReactComponent as EditSvg } from '@joinforma/ui/assets/icons/edit.svg';

// src
import Avatar from 'components/common/Avatar';
import { SettingsProfileProps } from './types';
import { BasicInfoSection } from './BasicInfoSection';
import { AdditionalInfoSection } from './AdditionalInfoSection';
import { EditInfoForm } from './EditInfoForm';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeadlineStyled = styled(Headline2)`
  margin-bottom: 24px;
`;

const TitleStyled = styled(Title1)``;

const TextStyled = styled(Body2)``;

const BodyStyled = styled(Body2)`
  color: ${({ theme }) => theme?.colors?.text?.secondary};
  padding-bottom: 28px;
`;

const ModalContentStyled = styled.div`
  display: flex;
  flex-direction: column;
`;

const ModalActionButtonStyled = styled(Button)`
  align-self: flex-end;
  margin-top: 48px;
`;

const AdditionalInfoTitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ExternalLinkIcon = styled(SvgIcon).attrs({
  icon: TopRightSvg,
  size: SvgIconSize.SMALL,
  fill: SvgIconColor.INTERACTIVE,
})``;

const ExternalLinkStyled = styled.div`
  height: 48px;
  font-size: 16px;
  font-weight: 700;
  border: 1px solid #3656dc;
  border-radius: 7px;
  text-align: center;
  display: flex;
  align-items: center;
  width: 253.33px;
  justify-content: center;
  a {
    color: ${({ theme }) => theme?.colors?.button?.action};
    margin-left: 19.31px;
    &:hover {
      color: ${({ theme }) => theme?.colors?.button?.action};
    }
  }
`;

export const SettingsProfile = ({
  showEditForm,
  showAddressMessageModal,
  showPhoneNumberModal,
  avatar_uploading,
  is_updating_employee,
  employee,
  is_company_pretax_enabled,
  mailing_address_description_placeholder,
  show_phone,
  onAvatarUpload,
  onEmployeeSubmit,
  setShowAddressMessageModal,
  setShowPhoneNumberModal,
  setShowEditForm,
}: SettingsProfileProps) => {
  const {
    first_name,
    last_name,
    email,
    date_of_birth,
    preferred_first_name = '',
    preferred_last_name = '',
    personal_email = '',
    phone,
    settings: { primary_residence, address, country },
  } = employee;
  const is_primary_residence_present = Object.keys(primary_residence).length;

  return (
    <Container>
      <HeadlineStyled>Profile</HeadlineStyled>
      <Avatar
        uploading={avatar_uploading}
        avatar_url={employee?.avatar_url}
        onAvatarUpload={onAvatarUpload}
      />
      <BasicInfoSection
        first_name={first_name}
        last_name={last_name}
        email={email}
        is_company_pretax_enabled={is_company_pretax_enabled}
        date_of_birth={date_of_birth}
        is_primary_residence_present={is_primary_residence_present}
        primary_residence={primary_residence}
      />

      <AdditionalInfoTitleContainer>
        <TitleStyled>Additional information</TitleStyled>
        {!showEditForm && (
          <Button
            onClick={() => setShowEditForm(true)}
            startIcon={EditSvg}
            variant={ButtonVariant.SECONDARY}
          >
            Edit
          </Button>
        )}
      </AdditionalInfoTitleContainer>
      {showEditForm ? (
        <EditInfoForm
          is_updating_employee={is_updating_employee}
          country={country}
          preferred_first_name={preferred_first_name}
          preferred_last_name={preferred_last_name}
          personal_email={personal_email}
          phone={phone}
          address={address}
          mailing_address_description_placeholder={
            mailing_address_description_placeholder
          }
          is_primary_residence_present={is_primary_residence_present}
          primary_residence={primary_residence}
          show_phone={show_phone}
          setShowEditForm={setShowEditForm}
          onEmployeeSubmit={onEmployeeSubmit}
        />
      ) : (
        <AdditionalInfoSection
          mailing_address_description_placeholder={
            mailing_address_description_placeholder
          }
          show_phone={show_phone}
          preferred_first_name={preferred_first_name}
          preferred_last_name={preferred_last_name}
          personal_email={personal_email}
          address={address}
          phone={phone}
        />
      )}
      <TitleStyled>Personal data</TitleStyled>
      <BodyStyled>
        The California Consumer Privacy Act (CCPA) gives California residents
        the right to make certain requests about their data.
      </BodyStyled>

      <ExternalLinkStyled>
        <ExternalLinkIcon />
        <a
          href="https://www.requesteasy.com/5dfe-0936"
          target="_blank"
          rel="noreferrer"
        >
          Manage personal data
        </a>
      </ExternalLinkStyled>

      <Modal
        title={`Mailing address`}
        open={showAddressMessageModal}
        onClose={() => setShowAddressMessageModal(false)}
      >
        <ModalContentStyled>
          <TextStyled>
            Enter all portions of your mailing address. If you&apos;d like to
            remove the mailing address from your profile, please clear all the
            mailing address fields.
          </TextStyled>
          <ModalActionButtonStyled
            onClick={() => setShowAddressMessageModal(false)}
          >
            Okay
          </ModalActionButtonStyled>
        </ModalContentStyled>
      </Modal>

      <Modal
        title={`Phone Number`}
        open={showPhoneNumberModal}
        onClose={() => setShowPhoneNumberModal(false)}
      >
        <ModalContentStyled>
          <TextStyled>Please enter a valid 10 digit phone number.</TextStyled>
          <ModalActionButtonStyled
            onClick={() => setShowPhoneNumberModal(false)}
          >
            Okay
          </ModalActionButtonStyled>
        </ModalContentStyled>
      </Modal>
    </Container>
  );
};
